/**
 * InventoryTable — Sortable, searchable table of inventory items.
 * Displays NetSuite item data: Item ID, Name, Type, On Hand, Total Value.
 * Follows the card + table pattern from OpenOrdersTable.tsx.
 *
 * Note: NS 25.2 removed quantityavailable, quantityonorder, quantitycommitted
 * from the item record. We use totalquantityonhand (aliased as quantityonhand).
 */

import { useState, useMemo } from "react";
import { NetSuiteRow } from "../utils/PowerBIApiCall";
import {
  formatCurrency as formatCurrencyShared,
  formatNumber as formatNumberShared,
} from "../utils/format";

interface InventoryTableProps {
  items: NetSuiteRow[];
}

type SortField =
  | "itemid"
  | "displayname"
  | "itemtype"
  | "quantityonhand"
  | "totalvalue";

type SortDir = "asc" | "desc";

function formatNumber(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  const num = Number(value);
  if (isNaN(num)) return String(value);
  return formatNumberShared(num);
}

function formatCurrency(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  const num = Number(value);
  if (isNaN(num)) return String(value);
  return formatCurrencyShared(num);
}

function SortIcon({
  field,
  currentField,
  currentDir,
}: {
  field: SortField;
  currentField: SortField;
  currentDir: SortDir;
}) {
  if (field !== currentField) {
    return (
      <svg
        className="w-3 h-3 text-gray-300 ml-1 inline"
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M5 8l5-5 5 5H5zm0 4l5 5 5-5H5z" />
      </svg>
    );
  }
  return (
    <svg
      className="w-3 h-3 text-wmp-500 ml-1 inline"
      fill="currentColor"
      viewBox="0 0 20 20"
    >
      {currentDir === "asc" ? (
        <path d="M5 12l5-5 5 5H5z" />
      ) : (
        <path d="M5 8l5 5 5-5H5z" />
      )}
    </svg>
  );
}

export function InventoryTable({ items }: InventoryTableProps) {
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState<SortField>("itemid");
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const filteredAndSorted = useMemo(() => {
    let result = items;

    // Filter by search
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (item) =>
          String(item.itemid || "")
            .toLowerCase()
            .includes(q) ||
          String(item.displayname || "")
            .toLowerCase()
            .includes(q),
      );
    }

    // Sort
    result = [...result].sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];

      // Nulls last
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      // Number comparison for numeric fields
      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortDir === "asc" ? aVal - bVal : bVal - aVal;
      }

      // String comparison
      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      return sortDir === "asc"
        ? aStr.localeCompare(bStr)
        : bStr.localeCompare(aStr);
    });

    return result;
  }, [items, search, sortField, sortDir]);

  const totalValue = useMemo(
    () =>
      items.reduce((sum, item) => {
        const val = Number(item.totalvalue);
        return sum + (isNaN(val) ? 0 : val);
      }, 0),
    [items],
  );

  const totalOnHand = useMemo(
    () =>
      items.reduce((sum, item) => {
        const val = Number(item.quantityonhand);
        return sum + (isNaN(val) ? 0 : val);
      }, 0),
    [items],
  );

  const thClass =
    "text-left text-2xs font-bold tracking-wider text-gray-500 uppercase px-4 py-2.5 cursor-pointer hover:text-gray-700 transition-colors select-none whitespace-nowrap";
  const thRightClass =
    "text-right text-2xs font-bold tracking-wider text-gray-500 uppercase px-4 py-2.5 cursor-pointer hover:text-gray-700 transition-colors select-none whitespace-nowrap";

  return (
    <div className="bg-white rounded-xl border border-gray-200/70 shadow-card overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-100">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2.5">
            <svg
              className="w-4.5 h-4.5 text-teal-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
              />
            </svg>
            <h2 className="text-lg font-semibold text-gray-900">
              Inventory Items
            </h2>
            <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-medium">
              {filteredAndSorted.length.toLocaleString()}
              {search && ` / ${items.length.toLocaleString()}`}
            </span>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <span className="text-xs text-gray-400 block">Total Value</span>
              <span className="text-sm font-bold text-teal-600 tabular-nums">
                {formatCurrency(totalValue)}
              </span>
            </div>
            <div className="text-right">
              <span className="text-xs text-gray-400 block">Total On Hand</span>
              <span className="text-sm font-bold text-gray-700 tabular-nums">
                {formatNumber(totalOnHand)}
              </span>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <input
            type="text"
            placeholder="Search by item ID or name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 text-sm border border-gray-200/70 rounded-lg focus:outline-none focus:ring-2 focus:ring-wmp-500/40 focus:border-wmp-500 transition-colors"
          />
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-wmp-50/70 border-b border-gray-200">
              <th className={thClass} onClick={() => handleSort("itemid")}>
                Item ID
                <SortIcon
                  field="itemid"
                  currentField={sortField}
                  currentDir={sortDir}
                />
              </th>
              <th className={thClass} onClick={() => handleSort("displayname")}>
                Display Name
                <SortIcon
                  field="displayname"
                  currentField={sortField}
                  currentDir={sortDir}
                />
              </th>
              <th className={thClass} onClick={() => handleSort("itemtype")}>
                Type
                <SortIcon
                  field="itemtype"
                  currentField={sortField}
                  currentDir={sortDir}
                />
              </th>
              <th
                className={thRightClass}
                onClick={() => handleSort("quantityonhand")}
              >
                On Hand
                <SortIcon
                  field="quantityonhand"
                  currentField={sortField}
                  currentDir={sortDir}
                />
              </th>
              <th
                className={thRightClass}
                onClick={() => handleSort("totalvalue")}
              >
                Total Value
                <SortIcon
                  field="totalvalue"
                  currentField={sortField}
                  currentDir={sortDir}
                />
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSorted.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  className="px-6 py-10 text-center text-sm text-gray-400"
                >
                  {search
                    ? "No items match your search."
                    : "No inventory items found."}
                </td>
              </tr>
            ) : (
              filteredAndSorted.map((item, idx) => (
                <tr
                  key={String(item.id ?? idx)}
                  className="border-b border-gray-50 last:border-b-0 hover:bg-wmp-50/50 transition-colors"
                >
                  <td className="px-4 py-2.5 text-sm font-mono text-gray-700 whitespace-nowrap">
                    {String(item.itemid ?? "—")}
                  </td>
                  <td className="px-4 py-2.5 text-sm text-gray-900 max-w-xs truncate">
                    {String(item.displayname ?? "—")}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-gray-500">
                    <span className="bg-gray-100 px-2 py-0.5 rounded-full">
                      {String(item.itemtype ?? "—")}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-sm text-gray-700 text-right tabular-nums">
                    {formatNumber(item.quantityonhand)}
                  </td>
                  <td className="px-4 py-2.5 text-sm font-medium text-teal-700 text-right tabular-nums">
                    {formatCurrency(item.totalvalue)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
